import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"
import NumberToWords from "../helpers/number_to_words"
import ProgressBar from "../helpers/progress_bar"
import urlParamsAsObject from "../helpers/url_params"

const NS = ".campaignUpdate"

// Paired with the shared `datatable` controller on the same `.content-box` mount across four
// views (subdomain/campaigns/{add,remove}/{gifts,orders}). This is one block of page-agnostic
// code: only one of the two datatables renders per view, the "add" views render a `filter[team]`
// select the "remove" views don't, `remove/gifts` alone renders `filter[order]`, and only the
// "remove" views carry `campaign-name`. Each of those is an optional target, guarded with
// `hasXTarget`.
//
// The gifts-added/removed modals are reached by plain document id lookup rather than a Stimulus
// target on purpose: subdomain/campaigns/add/orders.html.haml renders its modal as a SIBLING of
// `.content-box` while the other three nest it inside, so a target lookup would miss it there.
export default class extends Controller {
  // No `dateRangeInput` target: `window.ltDateRangePicker` reaches the input through its own
  // global jQuery selector, both on init and on the `disconnect()` cleanup below.
  //
  // `senderFilter`/`collectionFilter`/`teamFilter` are also each tagged with the generic `filter`
  // target (space-separated, alongside their specific name) so `reload()` can enumerate "every
  // filter select" without knowing their names -- each still needs its own specific target too,
  // since select2 init in `connect()` passes a different placeholder per field. `textFilter`
  // (the `filter[order]` field, remove/gifts view only) is the text-input equivalent; its keyup
  // binding is a `data-action` now (see `debouncedReload`), so nothing else needs to reach it by
  // name.
  //
  // `checkbox` covers both `.build-checkbox` and `.gift-checkbox` -- the row-selection checkboxes
  // rendered by BuildDecorator#campaign_datatable_checkbox_v2 / GiftDecorator#campaign_datatable_checkbox.
  // Only one of the two datatables (and so only one checkbox class) exists per view; Stimulus's
  // target list only ever contains whichever one the page actually rendered.
  static get targets() {
    return ["table", "senderFilter", "collectionFilter", "teamFilter", "filter", "textFilter", "updateCampaignButton", "checkbox"]
  }

  // `begin`/`end` are not Stimulus values: `window.ltDateRangePicker` reads those data attributes
  // itself for its "All Time" fallback, and nothing here needs them typed.
  static get values() {
    return { campaignName: String }
  }

  connect() {
    this.onDraw = this.onDraw.bind(this)

    // `draw.dt` is the DataTable plugin's own event namespace, announced through jQuery -- stays
    // on jQuery's side same as the `change` delegate below.
    $(this.tableTarget).on(`draw.dt${NS}`, this.onDraw)

    $(this.senderFilterTarget).select2({ placeholder: "Sender", width: "100%" })
    $(this.collectionFilterTarget).select2({ placeholder: "Collection", width: "100%" })
    if (this.hasTeamFilterTarget) {
      $(this.teamFilterTarget).select2({ placeholder: "Team", width: "100%" })
    }

    // select2 announces a pick with jQuery's `.trigger("change")`, not a native dispatch, so this
    // has to stay on jQuery's event bus rather than becoming a data-action.
    $(this.element).on(`change${NS}`, ".datatable-moveable-filter", () => this.reload())

    // initialize expects a jQuery-wrapped element; this.element is a plain DOM node.
    window.ltDateRangePicker.initialize($(this.element), {
      buttonClasses: "btn btn-main btn-md",
      cancelButtonClasses: "btn-ghost",
    }, (newRange) => this.reload({ range: newRange }))
    window.ltDateRangePicker.setPickerLabel()
  }

  disconnect() {
    $(this.tableTarget).off(NS)
    $(this.element).off(NS)

    ;[this.senderFilterTarget, this.collectionFilterTarget,
      this.hasTeamFilterTarget && this.teamFilterTarget].filter(Boolean).forEach((el) => {
      if ($(el).data("select2")) { $(el).select2("destroy") }
    })

    // The daterangepicker plugin stores its instance in jQuery's own data cache under this key,
    // not as a DOM attribute -- reading it back requires jQuery's `.data()`.
    const picker = $("input[name='activity-date-range-picker']").data("daterangepicker")
    if (picker) { picker.remove() }
  }

  // Wired via data-action on the select-all checkbox itself (injected by `onDraw`, below), listed
  // before `onCheckboxChange` on that element so this handler's DOM writes land before
  // `onCheckboxChange` reads `this.checkboxTargets` for the button's disabled state.
  onSelectAllChange(event) {
    const checked = event.currentTarget.checked
    this.checkboxTargets.forEach((el) => { el.checked = checked })
  }

  // Wired via data-action on both the select-all checkbox and every row checkbox (the latter set
  // by BuildDecorator#campaign_datatable_checkbox_v2 / GiftDecorator#campaign_datatable_checkbox).
  onCheckboxChange(event) {
    const anyChecked = this.checkboxTargets.some((el) => el.checked)
    this.updateCampaignButtonTarget.disabled = !anyChecked
  }

  // Wired via data-action on the `filter[order]` text input (remove/gifts view only).
  debouncedReload(event) {
    clearTimeout(this.orderFilterTimer)
    this.orderFilterTimer = setTimeout(() => this.reload(), 500)
  }

  // Wired via data-action on #add-to-campaign-button.
  onAddClick(event) {
    const button = event.currentTarget
    button.setAttribute("disabled", "disabled")
    const externalIds = this.getSelectedIds()

    this.hideSelectedRows()
    this.setDisplayed("#gifts-added-modal .loading", true)
    this.setDisplayed("#gifts-added-modal .success", false)
    this.setDisplayed("#gifts-added-modal .modal-header", false)
    this.setDisplayed("#gifts-added-modal .modal-footer", false)
    $("#gifts-added-modal").modal("show")

    const options = RequestHelper.defaultPostOptions()
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" })
    options.body = JSON.stringify({ ids: externalIds })

    fetch(button.dataset.url, options).then((response) => {
      button.removeAttribute("disabled")

      if (response.ok) {
        response.json().then((data) => {
          this.setDisplayed("#gifts-added-modal .loading", false)
          this.setDisplayed("#gifts-added-modal .success", true)
          this.setDisplayed("#gifts-added-modal .modal-header", true)
          this.setDisplayed("#gifts-added-modal .modal-footer", true)
          // `data.message` is render_to_string(partial: "subdomain/campaigns/add/added_to_campaign")
          // from Subdomain::Campaigns::AddController#create -- our own server-rendered partial. It
          // interpolates only a fixed title string and a Rails `pluralize()` count (see the
          // partial), never `raw`/`html_safe`/`!=`, and carries no user- or campaign-entered text.
          document.querySelector("#gifts-added-modal .description").innerHTML = data.message
          document.querySelectorAll("input.select-all").forEach((el) => { el.checked = false })
          this.reload()
        })
      } else {
        console.warn(response)
        // Bring back the rows since the request failed
        this.checkboxTargets.filter((el) => el.checked).forEach((el) => {
          el.closest("tr").style.display = ""
        })
      }
    })
  }

  // Wired via data-action on #remove-from-campaign-button.
  onRemoveClick(event) {
    const button = event.currentTarget
    const externalIds = this.getSelectedIds()
    const campaignName = this.campaignNameValue
    const resource = button.dataset.resource

    // `campaignName` is org-entered (Campaign#name via CampaignDecorator#sanitized_name, which
    // strips "{{"/"}}" but does not HTML-escape). Building nodes and setting the name via
    // `textContent` keeps it inert regardless of what characters it contains -- see S4 site #2 in
    // the hardening report for why this one couldn't just be commented like the other two.
    const description = document.querySelector("#gifts-removed-modal .confirm.description")
    description.replaceChildren(this.buildRemoveDescription(externalIds.length, resource, campaignName))

    this.setDisplayed("#gifts-removed-modal .confirm", true)
    this.setDisplayed("#gifts-removed-modal .loading", false)
    this.setDisplayed("#gifts-removed-modal .success", false)
    $("#gifts-removed-modal").modal("show")
  }

  // Unlike how we set the description upon success using a server response, we define the
  // message here so we can avoid a server call.
  buildRemoveDescription(count, resource, campaignName) {
    const p = document.createElement("p")
    const lead = count === 1 ?
      `One ${resource.slice(0, -1)} is` :
      `${NumberToWords.toWords(count)} ${resource} are`
    p.appendChild(document.createTextNode(`${lead} set to be removed from `))
    const strong = document.createElement("b")
    strong.textContent = campaignName
    p.appendChild(strong)
    p.appendChild(document.createTextNode(". Further changes will not take effect unless they are added again."))
    return p
  }

  // Wired via data-action on #confirm-remove-button.
  onConfirmRemoveClick(event) {
    const button = event.currentTarget
    const externalIds = this.getSelectedIds()
    const modal = button.closest(".modal")

    this.hideSelectedRows()
    modal.classList.add("no-header")
    this.setDisplayed("#gifts-removed-modal .loading", true)
    this.setDisplayed("#gifts-removed-modal .confirm", false)
    this.setDisplayed("#gifts-removed-modal .success", false)
    $("#gifts-removed-modal").modal("show")

    const options = RequestHelper.defaultPostOptions()
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" })
    options.body = JSON.stringify({ ids: externalIds })

    fetch(button.dataset.url, options).then((response) => {
      if (response.ok) {
        response.json().then((data) => {
          this.setDisplayed("#gifts-removed-modal .loading", false)
          this.setDisplayed("#gifts-removed-modal .success", true)
          // `data.message` is render_to_string(partial: "subdomain/campaigns/remove/removed_from_campaign")
          // from Subdomain::Campaigns::RemoveController#destroy -- our own server-rendered partial,
          // the same shape as the add-flow partial above: a fixed title and a `pluralize()` count
          // only, never `raw`/`html_safe`/`!=`, no user- or campaign-entered text.
          document.querySelector("#gifts-removed-modal .success.description").innerHTML = data.message
          document.querySelectorAll("input.select-all").forEach((el) => { el.checked = false })
          this.reload()
        })
      } else {
        console.warn(response)
        // Bring back the rows since the request failed
        this.checkboxTargets.filter((el) => el.checked).forEach((el) => {
          el.closest("tr").style.display = ""
        })
      }
    })
  }

  // Checkbox ids are "build_<external_id>"/"gift_<external_id>", so splitting on "_" recovers the
  // external id.
  getSelectedIds() {
    return this.checkboxTargets.filter((el) => el.checked).map((el) => el.id.split("_")[1])
  }

  hideSelectedRows() {
    this.checkboxTargets.filter((el) => el.checked).forEach((el) => {
      // Hide the rows to give the illusion of removing from the list until the refresh handles it
      // server-side.
      el.closest("tr").style.display = "none"
    })
  }

  // Sets (or clears) the inline display of every element matching `selector` in the document --
  // matches jQuery's `.show()`/`.hide()` exactly for these elements: none of them carry a
  // stylesheet `display: none` rule, so there's no jQuery-3-vs-inline-style conflict to worry
  // about (see CONVENTIONS.md, "Why is this still jQuery?").
  setDisplayed(selector, visible) {
    document.querySelectorAll(selector).forEach((el) => { el.style.display = visible ? "" : "none" })
  }

  // Injects the select-all checkbox into the header, which errors if placed directly in the HTML
  // ("Cannot read property 'childNodes' of undefined"). The gifts table has a hidden leading `id`
  // column the orders table lacks, pushing its checkbox column one cell over.
  //
  // The injected checkbox's data-action attribute is picked up by Stimulus the moment this lands
  // in the DOM -- no separate wiring needed on every redraw.
  onDraw() {
    const headerSelector = this.tableTarget.id === "campaign-orders-datatable" ?
      "thead tr th:first-child" :
      "thead tr th:nth-child(2)"

    this.tableTarget.querySelector(headerSelector).innerHTML = `
        <div class="form-group">
            <input type='checkbox' name='select-all' class='select-all' data-action="change->campaign-update#onSelectAllChange change->campaign-update#onCheckboxChange" /></th>
        </div>
    `
  }

  reload(extraParams = {}) {
    ProgressBar.start()

    if (this.filterTargets.length > 0) {
      const filter = {}
      this.filterTargets.forEach((el) => {
        const key = el.getAttribute("name").replace(/(^.*\[|\].*$)/g, "")
        // select2 keeps the current selection on the plugin instance, not as plain DOM state, so
        // reading it still goes through jQuery.
        filter[key] = ($(el).select2("data") || []).map((option) => option.id)
      })
      extraParams["filter"] = filter
    }

    this.textFilterTargets.forEach((el) => {
      extraParams["filter"] = extraParams["filter"] || {}
      extraParams["filter"][el.getAttribute("name").replace(/(^.*\[|\].*$)/g, "")] = el.value
    })

    const params = Object.assign(this.currentParams(), extraParams)

    // `data-source` is the app-wide raw-attribute convention 15+ datatable-driving controllers
    // read/write directly (see CONVENTIONS.md, "Why isn't this data attribute a Value?") -- not a Values API
    // candidate.
    const replacement = `${this.tableTarget.getAttribute("data-source").split("?")[0]}?${$.param(params)}`
    this.tableTarget.setAttribute("data-source", replacement)
    // `.DataTable()` is the plugin's own accessor for its API object -- stays jQuery.
    $(this.tableTarget).DataTable().ajax.url(replacement).load(() => ProgressBar.stop())
  }

  currentParams() {
    return urlParamsAsObject(this.tableTarget.getAttribute("data-source"))
  }
}
